/**
 * @file RepOrderRequestForm.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description A rep's request to the office (Cam, 29 Oct 2026): "pick a
 *              customer from a searchable dropdown … item (previously ordered
 *              (most recent first), contracted, don't show archived, allow no
 *              item to be picked and for them to just free type something e.g.
 *              'Blend'), additives (multi-select), qty, delivery date/ date
 *              range (if not needed on a specific date), notes … a checkbox for
 *              reps to check if they want this to be a standing order, in which
 *              case they should add frequency/ interval etc."
 *
 *   Two modes:
 *     new    the rep dashboard's "Request an order" — customer first (the
 *            phone's full-screen picker, SearchableDropdown's sheet), then the
 *            rest appears; "Standing order" swaps the delivery for a schedule.
 *     amend  "Request a change" on an order or a standing order — customer
 *            fixed, everything filled in from it (GET …/prefill/…).
 *
 *   Each line: an item the customer has had (most recent first) or is
 *   contracted for, or "Type what it is" for free text; additives; the
 *   quantity and its unit (units are required on orders — the order grid's
 *   fixed units, the item's own odd one, or None; picking an item puts its
 *   unit on the line). The rules are utils/repOrderRequests (normaliseRequest,
 *   the server's own); the server checks everything again — the customer on
 *   the rep's patch, no archived item, additives as stored.
 *
 *   The form never carries data-write-action: it is one of the two writes a
 *   rep may make (middleware/readOnly.js carve-out, rep_order_requests.send).
 */

import React, { useEffect, useMemo, useState, useCallback, useId } from 'react';
import SearchableDropdown from './SearchableDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import DateInput from './DateInput';
import Input from './Input';
import Textarea from './Textarea';
import TickCheckbox from './TickCheckbox';
import { addressLine1 } from '../utils/formatAddress';
import { formatDateDDMMYY } from '../utils/dateFormatters';
import {
  normaliseRequest, errorsText, unitChoicesFor, WEEKDAYS, INTERVAL_TYPES, londonToday,
  isStandingKind, KIND_WORDS, NOTES_MAX, LINE_TEXT_MAX, MAX_LINES
} from '../utils/repOrderRequests';
import { repRequestsApi } from '../utils/repOrderRequestApi';
import './RepOrderRequests.css';

const TEXT_OPTION = '__text__';
let lineSeq = 0;
const newLine = (over = {}) => ({ key: `l${(lineSeq += 1)}`, item_id: '', textMode: false, text: '', quantity: '', unit: '', additives: [], ...over });

/** A request's line (prefill) as the form's. */
const lineFromRequest = (l) => newLine({
  item_id: l.item_id ? String(l.item_id) : '',
  textMode: !l.item_id && !!l.text,
  text: l.text || '',
  quantity: l.quantity === null || l.quantity === undefined ? '' : String(l.quantity),
  unit: l.unit_weight_kg === null || l.unit_weight_kg === undefined ? '' : String(Number(l.unit_weight_kg)),
  additives: (l.additives || []).map(a => String(a.additive_id)).filter(Boolean),
  snapshotItem: l.item_id ? { item_id: l.item_id, item_code: l.item_code, item_name: l.item_name } : null
});

/** A row of choices, each a 44px button on the phone (radio-like). */
function Choices({ value, options, onChange, label, disabled, className = '' }) {
  return (
    <div className={`ror-choices ${className}`.trim()} role="radiogroup" aria-label={label}>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={String(value) === String(o.value)}
          className={`ror-choice${String(value) === String(o.value) ? ' is-on' : ''}`}
          onClick={() => !disabled && onChange(o.value)}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RepOrderRequestForm({
  mode = 'new',
  prefill = null,
  accounts = [],
  additives = [],
  ready = true,
  notReadyText = '',
  inline = false,
  onSent,
  renderActions = null
}) {
  const uid = useId();
  const amend = mode === 'amend';
  const amendKind = amend ? prefill?.request?.kind : null;
  const today = londonToday();

  const [accountId, setAccountId] = useState(() => (prefill?.request?.account_id ? String(prefill.request.account_id) : ''));
  const [standing, setStanding] = useState(() => (amend ? isStandingKind(amendKind) : false));
  const [lines, setLines] = useState(() => (prefill?.request?.lines?.length ? prefill.request.lines.map(lineFromRequest) : [newLine()]));
  const [deliveryType, setDeliveryType] = useState('date');
  const [date, setDate] = useState(() => (prefill?.request?.delivery?.type === 'date' ? prefill.request.delivery.date : ''));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [schedule, setSchedule] = useState(() => ({
    schedule_type: prefill?.request?.schedule?.schedule_type || 'weekly',
    day_of_week: prefill?.request?.schedule?.day_of_week || 1,
    interval_value: prefill?.request?.schedule?.interval_value ? String(prefill.request.schedule.interval_value) : '',
    interval_type: prefill?.request?.schedule?.interval_type || 'weeks'
  }));
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState({ previous: [], contracted: [], loading: false, error: null });
  const [errors, setErrors] = useState([]);
  const [sending, setSending] = useState(false);
  const [sentNote, setSentNote] = useState(null);

  // The customer's products: what they have had (most recent first) and are contracted for.
  useEffect(() => {
    if (!accountId) { setItems({ previous: [], contracted: [], loading: false, error: null }); return undefined; }
    let cancelled = false;
    setItems(prev => ({ ...prev, loading: true, error: null }));
    repRequestsApi.items(accountId)
      .then(d => { if (!cancelled) setItems({ previous: d.previous || [], contracted: d.contracted || [], loading: false, error: null }); })
      .catch(e => { if (!cancelled) setItems({ previous: [], contracted: [], loading: false, error: e.message }); });
    return () => { cancelled = true; };
  }, [accountId]);

  const itemById = useMemo(() => {
    const map = new Map();
    [...items.contracted, ...items.previous].forEach(i => map.set(String(i.item_id), i));
    return map;
  }, [items]);

  const accountOptions = useMemo(() => accounts.map(a => {
    const line1 = addressLine1(a.address);
    return {
      value: String(a.id),
      badge: a.account_no || '',
      label: line1 ? `${a.company_name} (${line1})` : a.company_name,
      searchText: `${a.account_no || ''} ${a.company_name} ${line1 || ''}`
    };
  }), [accounts]);

  const contractedIds = useMemo(() => new Set(items.contracted.map(i => String(i.item_id))), [items.contracted]);

  /** The item choices for one line: its own item first when it is on neither list (a change). */
  const itemOptionsFor = useCallback((line) => {
    const opts = [{
      value: TEXT_OPTION,
      sticky: true,
      label: 'Type what it is instead (e.g. "Blend")',
      renderLabel: (
        <span className="ror-opt">
          <span className="ror-opt-name">Type what it is instead</span>
          <span className="ror-opt-sub">For a product not listed — e.g. "Blend"</span>
        </span>
      ),
      searchText: 'type free text blend other'
    }];
    const seen = new Set();
    const add = (i, sub, tag) => {
      const v = String(i.item_id);
      if (seen.has(v)) return;
      seen.add(v);
      opts.push({
        value: v,
        badge: i.item_code || '',
        label: i.item_name,
        renderLabel: (
          <span className="ror-opt">
            <span className="ror-opt-name">{i.item_name}</span>
            {sub && <span className="ror-opt-sub">{sub}</span>}
          </span>
        ),
        tag,
        searchText: `${i.item_code || ''} ${i.item_name}`
      });
    };
    if (line.snapshotItem && !itemById.has(String(line.snapshotItem.item_id))) {
      add(line.snapshotItem, 'On the order now', null);
    }
    items.previous.forEach(i => add(i,
      `Last had ${formatDateDDMMYY(i.last_bought)}${i.times > 1 ? ` · ${i.times} times` : ''}`,
      contractedIds.has(String(i.item_id)) ? 'Contract' : null));
    items.contracted.forEach(i => add(i,
      `Contract${i.contract_ref ? ` ${i.contract_ref}` : ''}${i.until ? ` until ${formatDateDDMMYY(i.until)}` : ''}`,
      'Contract'));
    return opts;
  }, [items, itemById, contractedIds]);

  const additiveOptions = useMemo(() => additives.map(a => ({
    value: String(a.id),
    label: `${a.code ? `${a.code} ` : ''}${a.name}`,
    searchText: `${a.code || ''} ${a.name} ${a.supplier_name || ''}`
  })), [additives]);

  const patchLine = (key, patch) => setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const pickItem = (line, value) => {
    if (value === TEXT_OPTION) {
      patchLine(line.key, { item_id: '', textMode: true });
      return;
    }
    const item = itemById.get(String(value)) || (line.snapshotItem && String(line.snapshotItem.item_id) === String(value) ? line.snapshotItem : null);
    const unit = item && item.default_unit_kg !== null && item.default_unit_kg !== undefined ? String(item.default_unit_kg) : line.unit;
    patchLine(line.key, { item_id: String(value), textMode: false, text: '', unit });
  };

  // A standing order is one product (its one quantity).
  const toggleStanding = (on) => {
    setStanding(on);
    if (on) setLines(prev => prev.slice(0, 1));
  };

  const kind = amend ? amendKind : (standing ? 'standing_order' : 'order');

  const body = () => ({
    kind,
    account_id: accountId ? Number(accountId) : null,
    ...(amend && prefill?.request?.order_id ? { order_id: prefill.request.order_id } : {}),
    ...(amend && prefill?.request?.standing_order_id ? { standing_order_id: prefill.request.standing_order_id } : {}),
    lines: lines.map(l => ({
      item_id: l.textMode ? null : (l.item_id ? Number(l.item_id) : null),
      text: l.textMode ? l.text : null,
      quantity: l.quantity,
      unit_weight_kg: l.unit,
      additives: l.additives.map(id => ({ additive_id: Number(id) }))
    })),
    delivery: isStandingKind(kind) ? null
      : (deliveryType === 'range' ? { type: 'range', from, to } : { type: 'date', date }),
    schedule: isStandingKind(kind) ? {
      schedule_type: schedule.schedule_type,
      day_of_week: schedule.day_of_week,
      interval_value: schedule.interval_value,
      interval_type: schedule.interval_type
    } : null,
    notes
  });

  const reset = () => {
    setLines([newLine()]);
    setDate(''); setFrom(''); setTo('');
    setNotes('');
    setStanding(false);
    setSchedule({ schedule_type: 'weekly', day_of_week: 1, interval_value: '', interval_type: 'weeks' });
    setErrors([]);
  };

  const send = async () => {
    const b = body();
    const checked = normaliseRequest(b, { today, keepDate: amend ? prefill?.keep_date || null : null });
    if (!checked.request) { setErrors(checked.errors); return; }
    setErrors([]);
    setSending(true);
    try {
      const { request } = await repRequestsApi.send(b);
      if (!amend) {
        const account = accounts.find(a => String(a.id) === String(accountId));
        setSentNote(`Sent — ${KIND_WORDS[request.kind].toLowerCase()} for ${account ? account.company_name : 'the customer'}. The office will see it at the top of Sales Orders.`);
        reset();
        setAccountId('');
      }
      if (onSent) onSent(request);
    } catch (e) {
      setErrors(e.errors && e.errors.length ? e.errors : [{ field: 'server', text: e.message }]);
    } finally {
      setSending(false);
    }
  };

  const errorFor = (field) => errors.find(e => e.field === field || e.field.startsWith(`${field}.`));
  const errorOn = (prefix) => errors.filter(e => e.field === prefix || e.field.startsWith(prefix));

  if (!ready) {
    return <div className="ror-not-ready">{notReadyText}</div>;
  }

  const showRest = amend || !!accountId;
  const account = amend ? prefill?.account : null;

  return (
    <div className={`rep-order-request-form${inline ? ' is-inline' : ''}`}>
      {sentNote && !accountId && (
        <div className="ror-sent" role="status">
          <span className="material-symbols-rounded" aria-hidden="true">check_circle</span>
          <span>{sentNote}</span>
          <button type="button" className="ror-link" onClick={() => setSentNote(null)}>OK</button>
        </div>
      )}

      <div className="ror-field">
        <span className="ror-label">Customer</span>
        {amend ? (
          <div className="ror-fixed">
            {account?.account_no && <span className="account-badge-inline">{account.account_no}</span>}
            <span>{account?.company_name}</span>
          </div>
        ) : (
          <SearchableDropdown
            value={accountId}
            onChange={(v) => { setAccountId(v); setSentNote(null); setLines([newLine()]); }}
            options={accountOptions}
            placeholder="Choose a customer…"
            searchPlaceholder="Search by # or name…"
          />
        )}
      </div>

      {!amend && showRest && (
        <div className="ror-field ror-standing">
          <TickCheckbox
            id={`${uid}-standing`}
            checked={standing}
            onChange={(e) => toggleStanding(e.target.checked)}
            label="Make this a standing order"
            size={18}
          />
          {standing && <span className="ror-hint">A standing order is for one product, delivered on a schedule.</span>}
        </div>
      )}

      {showRest && (
        <>
          {lines.map((line, index) => {
            const item = line.item_id ? (itemById.get(String(line.item_id)) || line.snapshotItem) : null;
            const lineErrors = errorOn(`lines.${index}`);
            return (
              <div className="ror-line" key={line.key}>
                <div className="ror-line-head">
                  <span className="ror-label">{lines.length > 1 ? `Product ${index + 1}` : 'Product'}</span>
                  {lines.length > 1 && (
                    <button type="button" className="ror-icon-btn" onClick={() => setLines(prev => prev.filter(l => l.key !== line.key))}
                      aria-label={`Remove product ${index + 1}`} title="Remove this product">
                      <span className="material-symbols-rounded">close</span>
                    </button>
                  )}
                </div>
                {line.textMode ? (
                  <div className="ror-text-line">
                    <Input
                      value={line.text}
                      onChange={(e) => patchLine(line.key, { text: e.target.value.slice(0, LINE_TEXT_MAX) })}
                      placeholder='What is it? e.g. "Blend"'
                      maxLength={LINE_TEXT_MAX}
                      aria-label="What the product is"
                    />
                    <button type="button" className="ror-link" onClick={() => patchLine(line.key, { textMode: false, text: '' })}>
                      Choose from the list instead
                    </button>
                  </div>
                ) : (
                  <SearchableDropdown
                    value={line.item_id}
                    onChange={(v) => pickItem(line, v)}
                    options={itemOptionsFor(line)}
                    placeholder={items.loading ? 'Loading their products…' : 'Choose a product…'}
                    searchPlaceholder="Search by code or name…"
                    loading={items.loading}
                  />
                )}
                {items.error && <div className="ror-error">{items.error}</div>}

                <div className="ror-sub">
                  <span className="ror-label">Additives</span>
                  <MultiSelectDropdown
                    value={line.additives}
                    onChange={(v) => patchLine(line.key, { additives: v })}
                    options={additiveOptions}
                    placeholder="None"
                    searchable
                    searchPlaceholder="Search additives…"
                    summarize={(v) => (v.length === 0 ? 'None'
                      : v.map(id => additiveOptions.find(o => o.value === id)?.label || id).join(', '))}
                    hideTags
                  />
                </div>

                <div className="ror-sub ror-qty">
                  <label className="ror-label" htmlFor={`${uid}-${line.key}-qty`}>Quantity</label>
                  <Input
                    id={`${uid}-${line.key}-qty`}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={line.quantity}
                    onChange={(e) => patchLine(line.key, { quantity: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="ror-sub">
                  <span className="ror-label">Unit</span>
                  <Choices
                    label="Unit"
                    value={line.unit}
                    options={unitChoicesFor(item?.unit_weight_kg)}
                    onChange={(v) => patchLine(line.key, { unit: v })}
                    className="ror-units"
                  />
                </div>
                {lineErrors.length > 0 && <div className="ror-error">{lineErrors.map(e => e.text).join('. ')}</div>}
              </div>
            );
          })}

          {!standing && !isStandingKind(kind) && lines.length < MAX_LINES && (
            <button type="button" className="ror-add-line" onClick={() => setLines(prev => [...prev, newLine()])}>
              <span className="material-symbols-rounded" aria-hidden="true">add</span>
              Add another product
            </button>
          )}
          {errorFor('lines') && !errorOn('lines.').length && <div className="ror-error">{errorFor('lines').text}</div>}

          {isStandingKind(kind) ? (
            <div className="ror-field">
              <span className="ror-label">How often</span>
              <Choices
                label="How often"
                value={schedule.schedule_type}
                options={[{ value: 'weekly', label: 'Weekly on a day' }, { value: 'interval', label: 'Every so often' }]}
                onChange={(v) => setSchedule(s => ({ ...s, schedule_type: v }))}
              />
              {schedule.schedule_type === 'weekly' ? (
                <Choices
                  label="Day"
                  value={schedule.day_of_week}
                  options={WEEKDAYS.map(d => ({ value: d.value, label: d.label.slice(0, 3) }))}
                  onChange={(v) => setSchedule(s => ({ ...s, day_of_week: Number(v) }))}
                  className="ror-days"
                />
              ) : (
                <div className="ror-every">
                  <span>Every</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    step="1"
                    value={schedule.interval_value}
                    onChange={(e) => setSchedule(s => ({ ...s, interval_value: e.target.value }))}
                    placeholder="2"
                    aria-label="How many"
                  />
                  <Choices
                    label="Days, weeks or months"
                    value={schedule.interval_type}
                    options={INTERVAL_TYPES.map(t => ({ value: t.value, label: t.label }))}
                    onChange={(v) => setSchedule(s => ({ ...s, interval_type: v }))}
                  />
                </div>
              )}
              {errorFor('schedule') && <div className="ror-error">{errorFor('schedule').text}</div>}
            </div>
          ) : (
            <div className="ror-field">
              <span className="ror-label">Delivery</span>
              <Choices
                label="Delivery"
                value={deliveryType}
                options={[{ value: 'date', label: 'On a date' }, { value: 'range', label: 'Between two dates' }]}
                onChange={setDeliveryType}
              />
              {deliveryType === 'date' ? (
                <DateInput value={date} onChange={(e) => setDate(e.target.value)} min={amend && prefill?.keep_date && prefill.keep_date < today ? prefill.keep_date : today} placeholderText="DD/MM/YYYY" />
              ) : (
                <div className="ror-range">
                  <DateInput value={from} onChange={(e) => setFrom(e.target.value)} min={today} placeholderText="From" />
                  <span className="ror-range-and">and</span>
                  <DateInput value={to} onChange={(e) => setTo(e.target.value)} min={from || today} placeholderText="To" />
                </div>
              )}
              {errorFor('delivery') && <div className="ror-error">{errorFor('delivery').text}</div>}
            </div>
          )}

          <div className="ror-field">
            <label className="ror-label" htmlFor={`${uid}-notes`}>Notes</label>
            <Textarea
              id={`${uid}-notes`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={NOTES_MAX}
              placeholder={amend ? 'What should change, and anything the office should know' : 'Anything the office should know'}
            />
          </div>

          {errors.length > 0 && errors.some(e => e.field === 'server' || e.field === 'kind' || e.field === 'account_id') && (
            <div className="ror-error ror-error-block" role="alert">{errorsText(errors.filter(e => ['server', 'kind', 'account_id'].includes(e.field)))}</div>
          )}

          {renderActions ? renderActions({ send, sending }) : (
            <div className="ror-actions">
              <button type="button" className="ror-send" onClick={send} disabled={sending}>
                <span className="material-symbols-rounded" aria-hidden="true">send</span>
                {sending ? 'Sending…' : 'Send to the office'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RepOrderRequestForm;
